import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CBadge, CButton, CCol, CFormInput, CFormLabel, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faFlag } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { referralRecord } from "@/constants/translation/referralRecord.translation";
import { masters } from "@/libs/routes";
import { setFormatDate } from "@/utils/dateFormate";
import { useReferralRecordList, useReferralRecordCountries, useMarkReferralFraudulent } from "@/hooks/useReferralRecord";
import { TNTable } from "@/components";
import CustomDropdown from "@/components/CustomDropdown";
import { showMarkFraudulentDialog } from "@/components/MarkFraudulentDialog";
import constants from "@/libs/constant";

const { dateTypes } = constants;

const ACTIVE_STATUS_FILTER_VALUES = ["all", "active", "not_active"];

const getActiveStatusBadgeClass = (status) => {
  switch (status) {
    case "active":
      return "success";
    default:
      return "secondary";
  }
};

const getRewardStatusBadgeClass = (status) => {
  switch (status) {
    case "rewarded":
      return "success";
    default:
      return "warning";
  }
};

const ReferralRecordList = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [referralRecordList, setReferralRecordList] = useState([]);
  const [paginationData, setPaginationData] = useState(null);

  const [currentPage, setCurrentPage] = useState(constants.pagination.defaultPage);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [activeStatusFilter, setActiveStatusFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableKey, setTableKey] = useState(0);
  const [sortField, setSortField] = useState("registration_date");
  const [sortOrder, setSortOrder] = useState(constants.sort.order.desc);

  const searchTimeout = useRef(null);
  const dateTimeout = useRef(null);
  const isFiltering = useRef(false);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      if (dateTimeout.current) {
        clearTimeout(dateTimeout.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setTempSearchText(searchValue);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      isFiltering.current = true;
      setSearchText(searchValue);
      setCurrentPage(1);
      setTableKey((prev) => prev + 1);

      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 400);
  };

  const handleFilterChange = (setter) => (e) => {
    isFiltering.current = true;
    setter(e.target.value);
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleSort = (field) => {
    isFiltering.current = true;

    if (sortField === field) {
      setSortOrder(sortOrder === constants.sort.order.asc ? constants.sort.order.desc : constants.sort.order.asc);
    } else {
      setSortField(field);
      setSortOrder(constants.sort.order.asc);
    }
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  // Exact date-input handling pattern cloned from src/pages/user/list/index.js (Registration
  // Date range must match the User Management list, not the plainer Subscribed Users one) —
  // debounced change + blur/click clear-detection with a 100ms timeout to catch the native
  // date input's "x" clear button.
  const handleDateChange = (dateType, value) => {
    if (dateTimeout.current) {
      clearTimeout(dateTimeout.current);
    }

    dateTimeout.current = setTimeout(() => {
      isFiltering.current = true;
      if (dateType === dateTypes.start) {
        if (value === "") {
          setStartDate("");
        } else {
          setStartDate(value);
          if (endDate && endDate < value) {
            setEndDate(value);
          }
        }
      } else {
        if (value === "") {
          setEndDate("");
        } else {
          if (value < startDate) {
            setEndDate(startDate);
          } else {
            setEndDate(value);
          }
        }
      }
      setCurrentPage(1);
      setTableKey((prev) => prev + 1);

      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 300);
  };

  const handleDateBlur = (dateType, e) => {
    if (e.target.value === "") {
      if (dateType === dateTypes.start) {
        setStartDate("");
      } else {
        setEndDate("");
      }
      setCurrentPage(1);
    }
  };

  const handleDateClick = (dateType, e) => {
    const currentValue = e.target.value;
    setTimeout(() => {
      if (e.target.value === "" && currentValue !== "") {
        if (dateType === dateTypes.start) {
          setStartDate("");
        } else {
          setEndDate("");
        }
        setCurrentPage(1);
      }
    }, 100);
  };

  const handleClearFilter = () => {
    isFiltering.current = true;

    setSearchText("");
    setTempSearchText("");
    setActiveStatusFilter("all");
    setCountryFilter("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const {
    data: listData,
    isLoading,
    refetch,
  } = useReferralRecordList({
    page: currentPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText.trim() && { search: searchText.trim() }),
    ...(activeStatusFilter && activeStatusFilter !== "all" && { active_status: activeStatusFilter }),
    ...(countryFilter && { country: countryFilter }),
    ...(startDate && { registration_date_from: startDate }),
    ...(endDate && { registration_date_to: endDate }),
    sort_by: sortField,
    order_by: sortOrder,
  });

  const { data: countriesData } = useReferralRecordCountries();

  const { mutate: doMarkFraudulent } = useMarkReferralFraudulent(() => {
    toast.success(t(referralRecord.view.messages.mark_fraudulent_success));
    refetch();
  });

  useEffect(() => {
    if (listData) {
      setReferralRecordList(listData.data?.referralRecordList || []);
      setPaginationData(listData.data?.pagination);
    }
  }, [listData]);

  const countryOptions = useMemo(() => {
    const options = [{ value: "", label: t(referralRecord.list.country_filter_placeholder) }];
    (countriesData?.data?.countries || []).forEach((country) => {
      options.push({ value: country, label: country });
    });
    return options;
  }, [countriesData, t]);

  const activeStatusOptions = useMemo(
    () =>
      ACTIVE_STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: t(referralRecord.list.active_status_filter[value]),
      })),
    [t],
  );

  const handleViewClick = (referralUuid) => {
    navigate(masters.referralRecords.view.replace(":id", referralUuid));
  };

  // Shared dialog (src/components/MarkFraudulentDialog.js), also used by the Referral Record
  // view page header action, so the confirmAlert + Formik markup only exists once.
  const handleMarkFraudulent = (referralUuid) => {
    showMarkFraudulentDialog({
      t,
      referralUuid,
      onConfirm: (uuid, reason) => doMarkFraudulent({ uuid, reason }),
    });
  };

  const sortableHeader = (label, field) => (
    <button
      type="button"
      className="table-sort-button"
      onClick={(event) => {
        event.stopPropagation();
        handleSort(field);
      }}
    >
      {label}
    </button>
  );

  const columns = useMemo(
    () => [
      {
        id: "referral_date",
        accessorKey: "referral_date",
        sortField: "registration_date",
        header: sortableHeader(t(referralRecord.list.table.referral_date_col_heading), "registration_date"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() ? setFormatDate(getValue()) : t(referralRecord.list.defaults.na)}</span>,
      },
      {
        id: "referrer_name",
        accessorKey: "referrer_name",
        sortField: "referrer_name",
        header: sortableHeader(t(referralRecord.list.table.referrer_name_col_heading), "referrer_name"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() || t(referralRecord.list.defaults.na)}</span>,
      },
      {
        id: "referrer_email",
        accessorKey: "referrer_email",
        sortField: "referrer_email",
        header: sortableHeader(t(referralRecord.list.table.referrer_email_col_heading), "referrer_email"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() || t(referralRecord.list.defaults.na)}</span>,
      },
      {
        id: "invited_name",
        accessorKey: "invited_name",
        sortField: "invited_name",
        header: sortableHeader(t(referralRecord.list.table.invited_name_col_heading), "invited_name"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() || t(referralRecord.list.defaults.na)}</span>,
      },
      {
        id: "invited_email",
        accessorKey: "invited_email",
        sortField: "invited_email",
        header: sortableHeader(t(referralRecord.list.table.invited_email_col_heading), "invited_email"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() || t(referralRecord.list.defaults.na)}</span>,
      },
      {
        id: "country",
        accessorKey: "country",
        header: t(referralRecord.list.table.country_col_heading),
        cell: ({ getValue }) => <span>{getValue() || t(referralRecord.list.defaults.na)}</span>,
      },
      {
        id: "city",
        accessorKey: "city",
        header: t(referralRecord.list.table.city_col_heading),
        cell: ({ getValue }) => <span>{getValue() || t(referralRecord.list.defaults.na)}</span>,
      },
      {
        id: "active_status",
        accessorKey: "active_status",
        header: t(referralRecord.list.table.active_status_col_heading),
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <CBadge color={getActiveStatusBadgeClass(value)}>
              {t(referralRecord.list.active_status_filter[value]) || value}
            </CBadge>
          );
        },
      },
      {
        id: "reward_status",
        accessorKey: "reward_status",
        header: t(referralRecord.list.table.reward_status_col_heading),
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <CBadge color={getRewardStatusBadgeClass(value)}>
              {t(referralRecord.list.reward_status[value]) || value}
            </CBadge>
          );
        },
      },
      {
        id: "action",
        accessorKey: "referral_uuid",
        header: t(referralRecord.list.table.action_col_heading),
        cell: ({ row }) => {
          const isFraudFlagged =
            row.original.is_fraud_flagged === true || row.original.is_fraud_flagged === constants.status.apiStatus.true;
          return (
            <div className="d-flex justify-content-center gap-3">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="referral-record-view-tooltip">{t(referralRecord.list.view_tooltip)}</Tooltip>}
              >
                <CButton
                  onClick={() => handleViewClick(row.original.referral_uuid)}
                  style={{
                    backgroundColor: "#212631",
                    borderColor: "#212631",
                    color: "white",
                    padding: "0.25rem 0.5rem",
                    fontSize: "0.875rem",
                  }}
                >
                  <FontAwesomeIcon icon={faEye} />
                </CButton>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="referral-record-flag-tooltip">
                    {isFraudFlagged
                      ? t(referralRecord.list.already_flagged_tooltip)
                      : t(referralRecord.list.flag_fraudulent_tooltip)}
                  </Tooltip>
                }
              >
                <span>
                  <CButton
                    disabled={isFraudFlagged}
                    onClick={() => handleMarkFraudulent(row.original.referral_uuid)}
                    color="danger"
                    style={{
                      padding: "0.25rem 0.5rem",
                      fontSize: "0.875rem",
                      opacity: isFraudFlagged ? 0.5 : 1,
                    }}
                  >
                    <FontAwesomeIcon icon={faFlag} />
                  </CButton>
                </span>
              </OverlayTrigger>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, sortField, sortOrder],
  );

  return (
    <>
      <CRow className="mb-4">
        <CCol lg={12} className="d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            placeholder={t(referralRecord.list.search_placeholder)}
            onChange={handleSearch}
            value={tempSearchText}
            className="form-control-sm"
            style={{ maxWidth: "380px" }}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{ maxWidth: "200px", backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
            value={activeStatusFilter}
            onChange={handleFilterChange(setActiveStatusFilter)}
            options={activeStatusOptions}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{ maxWidth: "200px", backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
            value={countryFilter}
            onChange={handleFilterChange(setCountryFilter)}
            options={countryOptions}
            placeholder={t(referralRecord.list.country_filter_placeholder)}
          />

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="startDate" className="m-0">
              {t(referralRecord.list.date_from_placeholder)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => handleDateChange(dateTypes.start, e.target.value)}
              onBlur={(e) => handleDateBlur(dateTypes.start, e)}
              onClick={(e) => handleDateClick(dateTypes.start, e)}
              className="form-control"
              style={{ width: "155px", height: "45px" }}
            />
          </div>

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="endDate" className="m-0">
              {t(referralRecord.list.date_to_placeholder)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="endDate"
              value={endDate}
              min={startDate}
              onChange={(e) => handleDateChange(dateTypes.end, e.target.value)}
              onBlur={(e) => handleDateBlur(dateTypes.end, e)}
              onClick={(e) => handleDateClick(dateTypes.end, e)}
              className="form-control"
              style={{ width: "155px", height: "45px" }}
            />
          </div>

          {(tempSearchText ||
            activeStatusFilter !== "all" ||
            countryFilter ||
            startDate ||
            endDate) && (
            <CButton color="primary" type="button" onClick={handleClearFilter} style={{ height: "45px" }}>
              {t(referralRecord.list.clear_filter_btn_text)}
            </CButton>
          )}
        </CCol>
      </CRow>

      <TNTable
        key={tableKey}
        columns={columns}
        data={referralRecordList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          if (isFiltering.current) {
            return;
          }
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        t={t}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
        sortField={sortField}
        sortOrder={sortOrder}
      />
    </>
  );
};

ReferralRecordList.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.referralRecord?.list,
    pathname: masters.referralRecords.list,
    active: true,
  },
];

export default ReferralRecordList;